import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import AdminLogin from './pages/AdminLogin';
import ChatApp from './pages/ChatApp';
import AdminPanel from './pages/AdminPanel';
import HiddenPanel from './pages/HiddenPanel';
import DevicesPage from './pages/DevicesPage';
import TelegramLinkPage from './pages/TelegramLinkPage';
import TelegramUserLogin from './pages/TelegramUserLogin';
import QuickSendPage from './pages/QuickSendPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { PinGate } from './components/PinGate';
import './App.css';

// Protected Route for regular users
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  return <PinGate>{children}</PinGate>;
}

// Protected Route for admin users
function AdminRoute({ children }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;
  if (!isAdmin) return <Navigate to="/app" replace />;
  return children;
}

// Auth redirect - if already logged in, redirect to app
function AuthRoute({ children }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (isAuthenticated) {
    return <Navigate to={isAdmin ? '/admin' : '/app'} replace />;
  }
  return children;
}

function LoadingScreen() {
  return (
    <div className="min-h-dvh bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* Auth Routes */}
      <Route path="/" element={<AuthRoute><LoginPage /></AuthRoute>} />
      <Route path="/admin/login" element={<AdminLogin />} />

      {/* User Routes */}
      <Route path="/app" element={<ProtectedRoute><ChatApp /></ProtectedRoute>} />
      <Route path="/app/devices" element={<ProtectedRoute><DevicesPage /></ProtectedRoute>} />
      <Route path="/app/telegram-link" element={<ProtectedRoute><TelegramLinkPage /></ProtectedRoute>} />
      <Route path="/app/telegram-user-login" element={<ProtectedRoute><TelegramUserLogin /></ProtectedRoute>} />
      <Route path="/app/quick-send" element={<ProtectedRoute><QuickSendPage /></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />

      {/* Hidden Panel - no navigation link, direct URL only */}
      <Route path="/admin/akis" element={<AdminRoute><HiddenPanel /></AdminRoute>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App dark">
      <ErrorBoundary fallbackMessage="Uygulama yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.">
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors closeButton />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
